/**
 * StateGlyph — die sieben Formen der Zustands-Grammatik als Inline-SVG.
 *
 * Form trägt, Farbe verstärkt nur (design-hig.md, „Barrierefreiheit"):
 *
 *   ok       gefüllte Scheibe + Haken
 *   running  gefüllte Scheibe + Dreieck (atmet, respektiert prefers-reduced-motion)
 *   failed   gefüllte Scheibe + Kreuz
 *   sealed   gestricheltes Siegel (Quadrat! andere Grundform) + Ausrufezeichen
 *   open     leerer Ring
 *   skipped  Linie mit 3 × 14-Riegel quer darin
 *   ghost    reine Hairline
 *
 * `skipped` ist bewusst NICHT so leicht gezeichnet wie Ghost: mit 13 von 36
 * Slots ist es der häufigste Zustand der Runs-Tabelle und muss in der
 * Größenordnung der Scheiben sichtbar sein. Ghost bleibt die reine Hairline —
 * die beiden dürfen nicht verschmelzen.
 *
 * Die Hairline von `skipped`/`ghost` läuft über die volle Glyphenbreite; die
 * Verbindung in den Lücken zeichnet `MicroFlow` (siehe flow.css, `--flow-gap`).
 */
import { cx } from "@conuti-das/prince-ui";
import type { FlowState } from "@/types/pipeline";
import { stateWord, type FlowVocabulary } from "./flowState";
import "./flow.css";

export interface StateGlyphProps {
  state: FlowState;
  /** Kantenlänge in px (viewBox bleibt 18 × 18). Default 18. */
  size?: number;
  /**
   * Sichtbarer Titel (Tooltip) und barrierefreier Name. Ohne Titel ist die
   * Glyphe `aria-hidden` — den Text trägt dann die Zeile bzw. der Slot-Kopf.
   */
  title?: string;
  /** Setzt `title` automatisch auf das Wort des Zustands. */
  labelled?: boolean;
  vocabulary?: FlowVocabulary;
  className?: string;
}

export function StateGlyph({
  state,
  size = 18,
  title,
  labelled,
  vocabulary = "run",
  className,
}: StateGlyphProps) {
  const name = title ?? (labelled ? stateWord(state, vocabulary) : undefined);
  return (
    <svg
      className={cx("flow-glyph", className)}
      data-state={state}
      width={size}
      height={size}
      viewBox="0 0 18 18"
      role={name ? "img" : undefined}
      aria-hidden={name ? undefined : true}
      aria-label={name}
      focusable="false"
    >
      {name && <title>{name}</title>}
      {shape(state)}
    </svg>
  );
}

function shape(state: FlowState) {
  switch (state) {
    case "ok":
      return (
        <>
          <circle className="fg-disc" cx="9" cy="9" r="7.5" />
          <path className="fg-mark" d="M5.4 9.2 L7.8 11.6 L12.7 6.4" />
        </>
      );
    case "running":
      return (
        <>
          <circle className="fg-disc" cx="9" cy="9" r="7.5" />
          <path className="fg-markf" d="M7.2 5.8 L12.4 9 L7.2 12.2 Z" />
        </>
      );
    case "failed":
      return (
        <>
          <circle className="fg-disc" cx="9" cy="9" r="7.5" />
          <path className="fg-mark" d="M6.2 6.2 L11.8 11.8 M11.8 6.2 L6.2 11.8" />
        </>
      );
    case "sealed":
      // Quadrat statt Kreis: „liegt nicht bei der Maschine" ist eine andere
      // Grundform, nicht nur eine andere Farbe.
      return (
        <>
          <rect className="fg-seal" x="1.6" y="1.6" width="14.8" height="14.8" rx="5" />
          <path className="fg-mark" d="M9 5.4 V9.6" />
          <circle className="fg-markf" cx="9" cy="12.2" r="1" />
        </>
      );
    case "open":
      return <circle className="fg-ring" cx="9" cy="9" r="7.5" />;
    case "skipped":
      // Der Riegel: 3 px breit, 13 px lang + runde Kappen = 3 × 14 quer in der
      // Linie. Bewusst schwer genug, um neben den Scheiben zu bestehen.
      return (
        <>
          <path className="fg-hair" d="M0 9 H18" />
          <path className="fg-bar" d="M9 2.5 V15.5" />
        </>
      );
    case "ghost":
    default:
      return <path className="fg-hair" d="M0 9 H18" />;
  }
}
